import { Checkbox, Col, Form, Input, message, Modal, Radio, Row, Select, Skeleton, Tree } from 'antd'
import FormItem from 'antd/es/form/FormItem'
import { ModalProps } from 'antd/es/modal'
import type { DataNode } from 'antd/es/tree'
import { isNil, uniq } from 'lodash'
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import {
  CreateUserInput,
  InviteUserInput,
  MeContext,
  UpdateUserInput,
  useCustomerQuery,
  useDefaultUserPermissions,
  useInviteUserMutation,
  useQueryCustomers,
  useQueryUser,
  User,
  UserPermissionFeature,
  useUpdateUserMutation,
} from '../../api'
import { OrganizationsPermissionsSelector } from './OrganizationsPermissionsSelector'
import { normalizeTopUserFlags, topUserOrganizations } from './topUserFlags'
import { ExternalAuthConnectionSelector } from './selectors/ExternalAuthConnectionSelector'
import { UserQSGroupsSelector } from './UserQSGroupsSelector'

type Props = {
  user?: User
  defaultCustomerId?: string
  onFinished: (user: User) => void
} & Pick<ModalProps, 'visible' | 'onCancel'>

export const UserManagementModal: React.FC<Props> = ({ user, visible, onCancel, onFinished, defaultCustomerId }) => {
  const { data: meData } = useContext(MeContext)
  const [newUserInput, setNewUserInput] = useState<UpdateUserInput | CreateUserInput>()
  const [form] = Form.useForm()
  const isTopUserValue = Form.useWatch('isTopUser', form)
  const isTopPowerUserValue = Form.useWatch('isTopPowerUser', form)
  const selectedCustomerId = Form.useWatch('customerId', form)

  const adminEditorPermission = useMemo(
    () => meData?.me.isAdmin || meData?.me.user.role === 'customer-success',
    [meData?.me],
  )

  const { data, loading: customersLoading } = useCustomerQuery(
    selectedCustomerId ? { variables: { input: { id: selectedCustomerId } } } : { skip: true },
  )
  const customer = data?.customer

  const organizations = useMemo(() => data?.customer.organizations || [], [data])
  const rootOrganizations = useMemo(() => organizations.filter(o => isNil(o.parentId)), [organizations])

  const customersRequest = useQueryCustomers({ variables: { input: { type: null } }, skip: !meData?.me.isAdmin })
  const customers = useMemo(
    () => (customersRequest.data ? customersRequest.data.customers : customer ? [customer] : []),
    [customersRequest, customer],
  )

  // User request to get org permissions
  const userRequest = useQueryUser(
    user && user.id ? { variables: { id: user.id }, fetchPolicy: 'network-only' } : { skip: true },
  )

  const [inviteUser, { loading: inviteUserLoading }] = useInviteUserMutation()
  const [updateUser, { loading: updateUserLoading }] = useUpdateUserMutation()

  useEffect(() => {
    form.setFieldsValue({ organizationIds: [] })
    const optionCustomer = customers.find(c => selectedCustomerId === c.id)
    const currentValue = form.getFieldValue('qsGroups') || []
    if (!optionCustomer) {
      return
    }

    if (
      !currentValue.length ||
      (currentValue.length === 1 && ['curvox_suppliers', 'curvox_users'].includes(currentValue[0]))
    ) {
      form.setFieldsValue({
        qsGroups: optionCustomer.type === 'supplier' ? ['curvox_suppliers'] : ['curvox_users'],
      })
    }
  }, [selectedCustomerId, customers, form])

  useEffect(() => {
    if (isTopUserValue) {
      form.setFields([
        {
          name: 'organizations',
          value: topUserOrganizations(rootOrganizations, isTopPowerUserValue),
        },
      ])
    } else {
      if (userRequest.data) {
        form.setFields([
          {
            name: 'organizations',
            value: userRequest.data.user.userOrganizations.map(userOrg => ({
              organizationId: userOrg.organizationsId,
              isPowerUser: userOrg.isPowerUser,
            })),
          },
        ])
      }
    }
  }, [isTopUserValue, form, rootOrganizations, isTopPowerUserValue, userRequest.data])

  useEffect(() => {
    const initCustomer = customers.find(c => c.id === defaultCustomerId)

    form.resetFields()
    form.setFieldsValue(
      user
        ? {
            customerId: user.customer?.id || user.customerId,
            email: user.email,
            firstName: user.firstName,
            lastName: user.lastName,
            organizations: userRequest.data
              ? userRequest.data.user.userOrganizations.map(userOrg => ({
                  organizationId: userOrg.organizationsId,
                  isPowerUser: userOrg.isPowerUser,
                }))
              : [],
            permissions: userRequest.data?.user.permissions?.can.map(ability => ability.value),
            qsGroups: user.qsGroups || [],
            isTopUser: user.isTopUser,
            isTopPowerUser: user.isTopPowerUser,
            externalAuthConnectionId: user.externalAuthConnectionId,
          }
        : {
            customerId: initCustomer?.id || defaultCustomerId,
            email: undefined,
            firstName: undefined,
            lastName: undefined,
            organizations: [],
            qsGroups: initCustomer?.type === 'supplier' ? ['curvox_suppliers'] : ['curvox_users'],
          },
    )
  }, [form, user, visible, customers, userRequest.data, defaultCustomerId])

  const submit = async () => {
    try {
      await form.validateFields()
    } catch (e) {
      return
    }

    const { externalAuthConnectionId, ...rest } = form.getFieldsValue()

    const input = {
      ...(user && user.id ? { id: user.id } : {}),
      ...{ customerId: '', email: '', organizations: [] }, // This is just to avoid the compiler to complain
      ...rest,
      // The checkboxes are only rendered (and so only sent) for admin editors
      ...(adminEditorPermission ? normalizeTopUserFlags(rest) : {}),
      externalAuthConnectionId: externalAuthConnectionId || null,
    }

    // Send to server
    if (!user || !user.id) {
      try {
        const invitedUser = await inviteUser({ variables: { input: input as InviteUserInput } })
        if (invitedUser?.data?.inviteUser) {
          onFinished(invitedUser.data.inviteUser)
          message.success('Invite new user successfully.')
          return
        }
      } catch (e) {
        message.error(e.message, 5)
      }
    } else {
      try {
        const updatedUser = await updateUser({ variables: { input: input as UpdateUserInput } })
        if (updatedUser?.data?.updateUser) {
          message.success('Update user successfully.')
          onFinished(updatedUser.data.updateUser)
        }
      } catch (e) {
        message.error(e.message)
      }
    }
  }

  return (
    <Modal
      visible={visible}
      onCancel={onCancel}
      onOk={submit}
      confirmLoading={
        updateUserLoading || inviteUserLoading || customersLoading || customersRequest.loading || userRequest.loading
      }
      title={user ? 'Edit User' : 'Invite New User'}>
      {userRequest.loading ? (
        <Skeleton />
      ) : (
        <Form
          form={form}
          size="middle"
          layout="vertical"
          onValuesChange={v => {
            setNewUserInput({
              ...newUserInput,
              ...v,
            })
          }}>
          <FormItem
            name="customerId"
            label="Customer"
            rules={[
              {
                required: true,
                message: 'Please select a customer',
              },
            ]}>
            <Select showSearch filterOption optionFilterProp="label" disabled={!!defaultCustomerId || !!user}>
              {customers.map(c => (
                <Select.Option key={c.id} value={c.id} label={c.name}>
                  {c.name}
                </Select.Option>
              ))}
            </Select>
          </FormItem>
          <FormItem label="External Authentication System" name="externalAuthConnectionId">
            <ExternalAuthConnectionSelector allowClear labelInValue={false} />
          </FormItem>
          <FormItem
            label="E-Mail"
            name="email"
            rules={[
              {
                type: 'email',
                message: 'The input is not valid email!',
              },
              {
                required: true,
                message: 'Please input the user email',
              },
            ]}>
            <Input />
          </FormItem>
          <Row gutter={8}>
            <Col span={12}>
              <FormItem
                label="First Name"
                name="firstName"
                rules={[
                  {
                    required: true,
                    message: 'Please input the user First Name',
                  },
                ]}>
                <Input />
              </FormItem>
            </Col>
            <Col span={12}>
              <FormItem
                label="Last Name"
                name="lastName"
                rules={[
                  {
                    required: true,
                    message: 'Please input the user Last Name',
                  },
                ]}>
                <Input />
              </FormItem>
            </Col>
          </Row>

          {adminEditorPermission ? (
            <Row gutter={8}>
              <Col span={12}>
                <FormItem name="isTopUser" valuePropName="checked">
                  <Checkbox>Top User</Checkbox>
                </FormItem>
              </Col>
              <Col span={12}>
                {isTopUserValue ? (
                  <FormItem name="isTopPowerUser" valuePropName="checked">
                    <Checkbox>Power User</Checkbox>
                  </FormItem>
                ) : null}
              </Col>
            </Row>
          ) : null}
          {selectedCustomerId && (
            <FormItem label="Organizations" name="organizations">
              <OrganizationsPermissionsSelector
                loading={customersLoading}
                disabled={isTopUserValue || customersLoading}
                organizations={organizations}
              />
            </FormItem>
          )}

          <FormItem label="QuickSight Groups" name="qsGroups">
            <UserQSGroupsSelector />
          </FormItem>

          {adminEditorPermission ? (
            <FormItem label="Permissions" name="permissions">
              <PermissionEditor loading={userRequest.loading} />
            </FormItem>
          ) : null}
        </Form>
      )}
    </Modal>
  )
}

const MUTUALLY_EXCLUSIVE_GROUPS: string[][] = [
  ['baskets', 'basketsReadonly'], // Either Edit or View for baskets
]

function applyMutuallyExclusiveGroups(values: string[], prevValues: string[], groups: string[][]): string[] {
  let result = [...values]
  for (const group of groups) {
    const checkedInGroup = result.filter(v => group.includes(v))
    if (checkedInGroup.length <= 1) {
      continue
    }

    const newlyClicked = checkedInGroup.find(v => !prevValues.includes(v))
    const toRemove = newlyClicked ? group.filter(k => k !== newlyClicked) : group.slice(1)
    result = result.filter(v => !toRemove.includes(v))
  }
  return result
}

const PermissionEditor: React.FC<{
  value?: UserPermissionFeature[]
  onChange?: (newValue: UserPermissionFeature[] | undefined) => void
  loading?: boolean
}> = ({ value, onChange, loading }) => {
  const [customizableState, setCustomizableState] = useState<string>('default')
  const [initCheck, setInitCheck] = useState(false)
  const prevCheckedRef = useRef<string[]>([])

  const { data: defaultUserPermissionsData, loading: defaultLoading } = useDefaultUserPermissions()

  const customizableOptions = [
    { label: 'Custom', value: 'custom' },
    { label: 'Default', value: 'default' },
  ]

  const checkedKeys = useMemo(() => userAbilitiesToCheckedKeys(value), [value])
  useEffect(() => {
    prevCheckedRef.current = checkedKeys.checked as string[]
  }, [checkedKeys.checked])
  const defaultPermissionsKeys = useMemo(
    () => userAbilitiesToCheckedKeys(defaultUserPermissionsData?.defaultPermissions.can.map(v => v.value)),
    [defaultUserPermissionsData?.defaultPermissions],
  )

  useEffect(() => {
    if (
      !initCheck &&
      !loading &&
      !defaultLoading &&
      value &&
      JSON.stringify(userAbilitiesToCheckedKeys(value)) !== JSON.stringify(defaultPermissionsKeys)
    ) {
      setCustomizableState('custom')
      setInitCheck(true)
    }
  }, [value, defaultPermissionsKeys, loading, defaultLoading, initCheck])

  const treeData: DataNode[] = useMemo(() => {
    // The "(All)" + disabled-children state only applies in default mode (whole tree is
    // disabled there anyway). In custom mode children must stay individually editable, so
    // the root Analyze/Activate/Enrich tokens must not disable them (#7206).
    const isDefault = customizableState === 'default'
    const currentValue = isDefault ? defaultUserPermissionsData?.defaultPermissions.can.map(c => c.value) : value
    const allAnalyze = isDefault && !!currentValue?.find(v => v === UserPermissionFeature.Analyze)
    const allActivate = isDefault && !!currentValue?.find(v => v === UserPermissionFeature.Activate)
    const allEnrich = isDefault && !!currentValue?.find(v => v === UserPermissionFeature.Enrich)

    return [
      {
        title: 'All permissions',
        key: 'all',
        checkable: false,
        children: [
          {
            title: allAnalyze ? 'Analytics (All)' : 'Analytics',
            key: 'Analyze',
            children: [
              { title: 'Contracts', key: 'contracts', disabled: allAnalyze },
              {
                title: 'Baskets',
                key: 'baskets_parent',
                checkable: false,
                disabled: allAnalyze,
                children: [
                  { title: 'Edit', key: 'baskets' },
                  { title: 'View', key: 'basketsReadonly' },
                ],
              },
              { title: 'Spending', key: 'spending', disabled: allAnalyze },
              { title: 'Reports', key: 'reports', disabled: allAnalyze },
            ],
          },
          {
            title: allActivate ? 'Strategic Sourcing (All)' : 'Strategic Sourcing',
            key: 'Activate',
            children: [
              ...['opportunities', 'sourcingPlans'].map(i => ({
                title: i,
                key: i,
                disabled: allActivate,
              })),
              {
                title: 'Explicit features',
                key: 'activate_explicit',
                checkable: false,
                children: [
                  {
                    title: 'Project Tracker',
                    key: 'projectTracker',
                  },
                  {
                    title: 'CX Project Tracker',
                    key: 'cxProjectTracker',
                  },
                  {
                    title: 'Initiatives',
                    key: 'initiatives',
                  },
                ],
              },
            ],
          },
          {
            title: allEnrich ? 'Enrich (all)' : 'Enrich',
            key: 'Enrich',
            children: [
              ...['teamQueue', 'catalog', 'importStudy'].map(i => ({
                title: i,
                key: i,
                disabled: allEnrich,
              })),
            ],
          },
          {
            // Flat top-level entitlements are true leaves (isLeaf) — with `children: []` rc-tree
            // treats them as parents and their controlled checked state doesn't render (#7206).
            title: 'Project Aperture',
            key: 'ProjectAperture',
            isLeaf: true,
          },
          {
            title: 'Rebate Optimizer',
            key: 'RebateOptimizer',
            isLeaf: true,
          },
        ],
      },
    ]
  }, [value, customizableState, defaultUserPermissionsData?.defaultPermissions])

  return (
    <div>
      <Radio.Group
        options={customizableOptions}
        value={customizableState}
        onChange={v => {
          setCustomizableState(v.target.value)
          if (v.target.value === 'default') {
            onChange && onChange([UserPermissionFeature.Null])
          } else {
            onChange && onChange(defaultUserPermissionsData?.defaultPermissions.can.map(i => i.value))
          }
        }}
        optionType="button"
        buttonStyle="solid"
      />
      <Tree
        disabled={customizableState === 'default'}
        checkStrictly
        onCheck={v => {
          const checked = (
            v as {
              checked: string[]
              halfChecked: string[]
            }
          ).checked
          const values = checked.filter(
            i => ['Analyze', 'Activate', 'Enrich', 'activate_explicit', 'baskets_parent'].indexOf(i) === -1,
          ) as UserPermissionFeature[]

          const filteredValues = applyMutuallyExclusiveGroups(
            values as string[],
            prevCheckedRef.current,
            MUTUALLY_EXCLUSIVE_GROUPS,
          ) as UserPermissionFeature[]
          prevCheckedRef.current = filteredValues as string[]

          setCustomizableState(
            (
              v as {
                checked: string[]
                halfChecked: string[]
              }
            ).checked.find(i => i === UserPermissionFeature.Null)
              ? 'default'
              : 'custom',
          )

          onChange && onChange(filteredValues)
        }}
        checkable
        defaultExpandAll
        treeData={treeData}
        checkedKeys={customizableState === 'custom' ? checkedKeys : defaultPermissionsKeys}
      />
    </div>
  )
}

const ANALYZE_FEATURE_KEYS = ['contracts', 'baskets', 'basketsReadonly', 'spending', 'reports']

export function userAbilitiesToCheckedKeys(value?: UserPermissionFeature[]) {
  const vals = (value || []) as string[]
  const analyzeExpanded = vals.includes('Analyze') ? vals.filter(v => ANALYZE_FEATURE_KEYS.includes(v as string)) : []
  return {
    checked: uniq([
      ...vals.filter(v => !['Analyze', 'Activate', 'Enrich'].includes(v as string)),
      ...analyzeExpanded,
      ...(vals.includes('Activate') ? vals.filter(v => ['opportunities', 'sourcingPlans'].includes(v as string)) : []),
      ...(vals.includes('Enrich')
        ? vals.filter(v => ['teamQueue', 'catalog', 'importStudy'].includes(v as string))
        : []),
    ]),
    halfChecked: [
      ...(vals.some(v => ANALYZE_FEATURE_KEYS.includes(v as string)) ? ['Analyze'] : []),
      ...(vals.some(v =>
        ['opportunities', 'sourcingPlans', 'projectTracker', 'cxProjectTracker', 'initiatives'].includes(v as string),
      )
        ? ['Activate']
        : []),
      ...(vals.some(v => ['teamQueue', 'catalog', 'importStudy'].includes(v as string)) ? ['Enrich'] : []),
    ],
  }
}
